import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useSearchParams } from 'react-router-dom';
import { ListingHoldReason } from '../../../__generated__/graphql';
import { getBrand } from '../../constants/brands';
import { Button } from '../../components/button';
import {
  Alert,
  Badge,
  Card,
  EmptyState,
  Input,
  LoadingBlock,
  PageContainer,
  PageHeader,
  Select,
  Stack,
  StatCard,
  StatGrid,
  TBody,
  TD,
  TDActions,
  TH,
  THead,
  TR,
  Table,
  TableWrap,
  Tabs,
} from '../../components/ui';
import {
  useCarsPendingRecallCheckQuery,
  useCarsWithoutRecallMatchQuery,
  useRecallJobStatusQuery,
  useUnlistedCarsQuery,
} from '../../services/listingHoldService/__generated__/queries.generated';
import { holdReasonLabel, recallJobModeText, unmatchedReasonText } from '../../utils/listingHold';
import {
  DialogCar,
  DialogHold,
  RecordCheckDialog,
  RelistDialog,
  TripsDialog,
  carLabel,
} from './dialogs';

type TabKey = 'unlisted' | 'pending' | 'unmatched';
const PAGE_SIZE = 25;

const Filters = styled.div`
  ${tw`flex flex-wrap items-end gap-3 p-4`}
`;
const FilterLabel = styled.label`
  ${tw`flex items-center gap-2 text-sm`}
  color: var(--color-text-secondary);
`;
const CarCell = styled.div`
  ${tw`flex flex-col min-w-0`}
`;
const Sub = styled.span`
  ${tw`text-xs`}
  color: var(--color-text-secondary);
`;

const REASON_TONE: Record<ListingHoldReason, 'error' | 'warning' | 'neutral'> = {
  [ListingHoldReason.OpenRecall]: 'error',
  [ListingHoldReason.Admin]: 'warning',
  [ListingHoldReason.Sold]: 'neutral',
  [ListingHoldReason.Host]: 'neutral',
};

const personName = (person?: { firstName?: string | null; lastName?: string | null } | null) =>
  [person?.firstName, person?.lastName].filter(Boolean).join(' ');

const when = (value?: string | null) => (value ? new Date(value).toLocaleString() : 'Never');

type Dialog =
  | { kind: 'relist'; car: DialogCar; hold: DialogHold }
  | { kind: 'check'; car: DialogCar }
  | { kind: 'trips'; car: DialogCar; holdReason: ListingHoldReason }
  | null;

/**
 * Spec 015 US-5, US-6, US-7: cars unlisted on this site (by hosts, admins, a
 * sale or a safety recall), cars waiting for a recall check, and cars the
 * hourly job cannot check. A brand admin sees the cars of hosts approved on
 * this site; the platform owner sees every car.
 */
export function AdminUnlistedCarsPage() {
  const brand = getBrand();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<TabKey>('unlisted');
  const [reason, setReason] = useState<ListingHoldReason | ''>('');
  const [needsActionOnly, setNeedsActionOnly] = useState(false);
  const [search, setSearch] = useState(searchParams.get('search') ?? '');
  const [offset, setOffset] = useState(0);
  const [dialog, setDialog] = useState<Dialog>(null);

  const unlisted = useUnlistedCarsQuery({
    variables: {
      filter: {
        reason: reason || null,
        needsActionOnly,
        search: search.trim() || null,
        limit: PAGE_SIZE,
        offset,
      },
    },
    fetchPolicy: 'cache-and-network',
  });
  const pending = useCarsPendingRecallCheckQuery({
    variables: { limit: 100, offset: 0 },
    fetchPolicy: 'cache-and-network',
  });
  const unmatched = useCarsWithoutRecallMatchQuery({ fetchPolicy: 'cache-and-network' });
  const job = useRecallJobStatusQuery({ fetchPolicy: 'cache-and-network' });

  const status = job.data?.recallJobStatus;
  const rows = unlisted.data?.unlistedCars.items ?? [];
  const total = unlisted.data?.unlistedCars.total ?? 0;
  const pendingCars = pending.data?.carsPendingRecallCheck ?? [];
  const unmatchedCars = unmatched.data?.carsWithoutRecallMatch ?? [];

  const tabItems = useMemo(
    () => [
      {
        key: 'unlisted' as const,
        label: 'Unlisted',
        count: unlisted.data?.unlistedCars.total,
        'data-testid': 'unlisted-tab-unlisted',
      },
      {
        key: 'pending' as const,
        label: 'Pending recall check',
        count: pending.data?.carsPendingRecallCheck.length,
        'data-testid': 'unlisted-tab-pending',
      },
      {
        key: 'unmatched' as const,
        label: "Can't check automatically",
        count: unmatched.data?.carsWithoutRecallMatch.length,
        'data-testid': 'unlisted-tab-unmatched',
      },
    ],
    [unlisted.data, pending.data, unmatched.data],
  );

  const error = unlisted.error ?? pending.error ?? unmatched.error ?? job.error;

  return (
    <PageContainer width="lg" canvas>
      <PageHeader
        eyebrow="Admin"
        title="Unlisted cars"
        description={`Cars guests cannot book on ${brand.name}, and safety recall checks.`}
      />
      <Stack>
        {error && <Alert tone="error">{error.message}</Alert>}
        <StatGrid $cols={3}>
          <StatCard
            label="Recall job"
            value={status ? recallJobModeText(status.mode) : '…'}
            hint={status?.lastError ? `Last error: ${status.lastError}` : undefined}
            data-testid="recall-job-mode"
          />
          <StatCard
            label="Last successful run"
            value={status ? when(status.lastSuccessAt) : '…'}
            hint={
              status
                ? `${status.modelYearsTracked} model years tracked, ${status.modelYearsFailing} failing`
                : undefined
            }
          />
          <StatCard
            label="Can't check automatically"
            value={status ? status.unmatchedCarCount : '…'}
            hint="Cars without make, model or year, or a model NHTSA does not list"
          />
        </StatGrid>

        <Tabs items={tabItems} value={tab} onChange={setTab} aria-label="Unlisted cars" />

        {tab === 'unlisted' && (
          <Card>
            <Filters>
              <Select
                aria-label="Reason"
                data-testid="unlisted-reason-filter"
                value={reason}
                onChange={event => {
                  setReason(event.target.value as ListingHoldReason | '');
                  setOffset(0);
                }}
              >
                <option value="">All reasons</option>
                <option value={ListingHoldReason.OpenRecall}>Safety recall</option>
                <option value={ListingHoldReason.Admin}>Unlisted by {brand.name}</option>
                <option value={ListingHoldReason.Sold}>Sold</option>
                <option value={ListingHoldReason.Host}>Unlisted by host</option>
              </Select>
              <Input
                aria-label="Search"
                placeholder="VIN, make, model or host email"
                data-testid="unlisted-search"
                value={search}
                onChange={event => {
                  setSearch(event.target.value);
                  setOffset(0);
                }}
              />
              <FilterLabel>
                <input
                  type="checkbox"
                  data-testid="unlisted-needs-action"
                  checked={needsActionOnly}
                  onChange={event => {
                    setNeedsActionOnly(event.target.checked);
                    setOffset(0);
                  }}
                />
                Needs action
              </FilterLabel>
            </Filters>
            {unlisted.loading && !unlisted.data && <LoadingBlock label="Loading unlisted cars" />}
            {unlisted.data && rows.length === 0 && (
              <EmptyState compact title="No unlisted cars" description="Nothing matches." />
            )}
            {rows.length > 0 && (
              <TableWrap>
                <Table>
                  <THead>
                    <TR>
                      <TH>Car</TH>
                      <TH>Host</TH>
                      <TH>Reason</TH>
                      <TH>Since</TH>
                      <TH $align="right">Future trips</TH>
                      <TH $align="right">Actions</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {rows.map(row => {
                      const hold = row.hold;
                      return (
                        <TR key={row.car.id} data-testid={`unlisted-row-${row.car.id}`}>
                          <TD>
                            <CarCell>
                              <a href={`/cars/host/view/${row.car.id}`}>{carLabel(row.car)}</a>
                              <Sub>{row.car.vin || 'No VIN'}</Sub>
                            </CarCell>
                          </TD>
                          <TD>
                            <CarCell>
                              <span>{row.hostName || '—'}</span>
                              <Sub>{row.hostEmail}</Sub>
                            </CarCell>
                          </TD>
                          <TD>
                            <CarCell>
                              <span>
                                <Badge tone={REASON_TONE[hold.reason]} size="sm">
                                  {holdReasonLabel(hold.reason, brand.name)}
                                </Badge>{' '}
                                {hold.pendingManualCheck && (
                                  <Badge tone="info" size="sm">
                                    Manual check
                                  </Badge>
                                )}
                              </span>
                              <Sub>
                                {hold.campaigns.map(c => c.campaignNumber).join(', ') ||
                                  hold.note ||
                                  ''}
                              </Sub>
                            </CarCell>
                          </TD>
                          <TD>
                            <CarCell>
                              <span>{new Date(hold.createdAt).toLocaleDateString()}</span>
                              <Sub>{hold.actorName ?? 'Automatic'}</Sub>
                            </CarCell>
                          </TD>
                          <TD $align="right">
                            {row.futureTripCount}{' '}
                            {row.needsAction && (
                              <Badge tone="warning" size="sm">
                                Needs action
                              </Badge>
                            )}
                          </TD>
                          <TDActions>
                            {row.futureTripCount > 0 && (
                              <Button
                                theme="secondary"
                                size="sm"
                                type="button"
                                text="Trips"
                                dataTestId={`unlisted-trips-${row.car.id}`}
                                onClick={() =>
                                  setDialog({
                                    kind: 'trips',
                                    car: row.car,
                                    holdReason: hold.reason,
                                  })
                                }
                              />
                            )}
                            <Button
                              theme="filled"
                              size="sm"
                              type="button"
                              text="Relist"
                              dataTestId={`unlisted-relist-${row.car.id}`}
                              onClick={() => setDialog({ kind: 'relist', car: row.car, hold })}
                            />
                          </TDActions>
                        </TR>
                      );
                    })}
                  </TBody>
                </Table>
              </TableWrap>
            )}
            {total > PAGE_SIZE && (
              <Filters>
                <Button
                  theme="secondary"
                  size="sm"
                  type="button"
                  text="Previous"
                  isDisabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                />
                <Sub>
                  {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
                </Sub>
                <Button
                  theme="secondary"
                  size="sm"
                  type="button"
                  text="Next"
                  isDisabled={offset + PAGE_SIZE >= total}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                />
              </Filters>
            )}
          </Card>
        )}

        {tab === 'pending' && (
          <Card>
            {pending.loading && !pending.data && <LoadingBlock label="Loading cars" />}
            {pending.data && pendingCars.length === 0 && (
              <EmptyState
                compact
                title="No cars waiting"
                description="Every listed car of a host on this site has a recall check."
              />
            )}
            {pendingCars.length > 0 && (
              <TableWrap>
                <Table>
                  <THead>
                    <TR>
                      <TH>Car</TH>
                      <TH>Host</TH>
                      <TH $align="right">Actions</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {pendingCars.map(car => (
                      <TR key={car.id} data-testid={`pending-row-${car.id}`}>
                        <TD>
                          <CarCell>
                            <span>{carLabel(car)}</span>
                            <Sub>{car.vin || 'No VIN'}</Sub>
                          </CarCell>
                        </TD>
                        <TD>
                          <CarCell>
                            <span>{personName(car.owner) || '—'}</span>
                            <Sub>{car.owner?.email}</Sub>
                          </CarCell>
                        </TD>
                        <TDActions>
                          <Button
                            theme="filled"
                            size="sm"
                            type="button"
                            text="Record check"
                            dataTestId={`pending-record-${car.id}`}
                            onClick={() => setDialog({ kind: 'check', car })}
                          />
                        </TDActions>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </TableWrap>
            )}
          </Card>
        )}

        {tab === 'unmatched' && (
          <Card>
            {unmatched.loading && !unmatched.data && <LoadingBlock label="Loading cars" />}
            {unmatched.data && unmatchedCars.length === 0 && (
              <EmptyState compact title="Every car can be checked automatically" />
            )}
            {unmatchedCars.length > 0 && (
              <TableWrap>
                <Table>
                  <THead>
                    <TR>
                      <TH>Car</TH>
                      <TH>Host</TH>
                      <TH>Why</TH>
                      <TH $align="right">Actions</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {unmatchedCars.map(({ car, reason: why }) => (
                      <TR key={car.id} data-testid={`unmatched-row-${car.id}`}>
                        <TD>
                          <CarCell>
                            <span>{carLabel(car)}</span>
                            <Sub>{car.vin || 'No VIN'}</Sub>
                          </CarCell>
                        </TD>
                        <TD>
                          <CarCell>
                            <span>{personName(car.owner) || '—'}</span>
                            <Sub>{car.owner?.email}</Sub>
                          </CarCell>
                        </TD>
                        <TD>{unmatchedReasonText(why)}</TD>
                        <TDActions>
                          <Button
                            theme="secondary"
                            size="sm"
                            type="button"
                            text="Record check"
                            onClick={() => setDialog({ kind: 'check', car })}
                          />
                        </TDActions>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </TableWrap>
            )}
          </Card>
        )}
      </Stack>

      {dialog?.kind === 'relist' && (
        <RelistDialog car={dialog.car} hold={dialog.hold} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'check' && (
        <RecordCheckDialog car={dialog.car} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'trips' && (
        <TripsDialog
          car={dialog.car}
          holdReason={dialog.holdReason}
          onClose={() => setDialog(null)}
        />
      )}
    </PageContainer>
  );
}

export default AdminUnlistedCarsPage;
